import { createContext, useState, useEffect, useContext } from "react";

export const ThemeContext = createContext();

const SYSTEM_THEME_QUERY = "(prefers-color-scheme: dark)";
const THEME_OPTIONS = new Set(["light", "dark", "system"]);

export const ThemeProvider = ({ children }) => {
  const getStoredInterfaceTheme = () => {
    try {
      const interfaceTheme = window.localStorage.getItem("interfaceTheme");
      if (THEME_OPTIONS.has(interfaceTheme)) {
        return interfaceTheme;
      }

      // Fallback to legacy 'theme' key if interfaceTheme not set
      const legacyTheme = window.localStorage.getItem("theme");
      if (legacyTheme === "dark" || legacyTheme === "light") {
        return legacyTheme;
      }

      return "light";
    } catch (e) {
      return "light";
    }
  };

  const getResolvedIsDarkMode = (interfaceTheme) => {
    if (interfaceTheme === "system") {
      return window.matchMedia(SYSTEM_THEME_QUERY).matches;
    }

    return interfaceTheme === "dark";
  };

  const [interfaceTheme, setInterfaceTheme] = useState(getStoredInterfaceTheme);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    getResolvedIsDarkMode(getStoredInterfaceTheme()),
  );

  // useEffect(() => {
  //   const storedTheme = window.localStorage.getItem("theme");
  //   if (storedTheme) {
  //     setIsDarkMode(storedTheme === "dark");
  //   } else {
  //     setIsDarkMode(false);
  //   }
  // }, []);

  const toggleTheme = (theme) => {
    const nextInterfaceTheme = THEME_OPTIONS.has(theme) ? theme : "light";
    setInterfaceTheme(nextInterfaceTheme);
    setIsDarkMode(getResolvedIsDarkMode(nextInterfaceTheme));
  };

  useEffect(() => {
    const resolvedTheme = isDarkMode ? "dark" : "light";
    document.documentElement.setAttribute("data-theme", resolvedTheme);
    window.localStorage.setItem("theme", resolvedTheme);
  }, [isDarkMode]);

  useEffect(() => {
    let mediaQueryList;

    try {
      window.localStorage.setItem("interfaceTheme", interfaceTheme);
      setIsDarkMode(getResolvedIsDarkMode(interfaceTheme));

      if (interfaceTheme !== "system") {
        return undefined;
      }

      mediaQueryList = window.matchMedia(SYSTEM_THEME_QUERY);

      const handler = (event) => setIsDarkMode(event.matches);
      if (mediaQueryList.addEventListener) {
        mediaQueryList.addEventListener("change", handler);
      } else if (mediaQueryList.addListener) {
        mediaQueryList.addListener(handler);
      }

      return () => {
        if (mediaQueryList.removeEventListener) {
          mediaQueryList.removeEventListener("change", handler);
        } else if (mediaQueryList.removeListener) {
          mediaQueryList.removeListener(handler);
        }
      };
    } catch (e) {
      return undefined;
    }
  }, [interfaceTheme]);

  return (
    <ThemeContext.Provider value={{ isDarkMode, interfaceTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
